/** Demo Mode only - Finance area shell. Consolidates the former FinanceDashboard,
 * ProfitAndLoss and CommercialPerformance pages into a single "Finance Performance"
 * experience with 5 tabs sharing one scope selector + filter bar (see
 * FinanceFiltersContext.tsx and financeData.ts for details/assumptions).
 *
 * Tab state is a URL search param ("tab") so tabs are bookmarkable/shareable, following
 * the same query-param pattern used for scope/filter state. Content is rendered
 * conditionally (only the active tab mounts) since each tab is data/chart heavy. */

import { useCallback } from 'react';
import { Tab, Tabs } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { FinanceFiltersProvider } from './FinanceFiltersContext';
import { FinanceTab } from './types';
import FinanceFilterBar from './components/FinanceFilterBar';
import OverviewTab from './tabs/OverviewTab';
import ForecastTab from './tabs/ForecastTab';
import ProfitAndLossTab from './tabs/ProfitAndLossTab';
import ContractsTab from './tabs/ContractsTab';
import EbitAnalysisTab from './tabs/EbitAnalysisTab';

const TAB_ORDER = [
  FinanceTab.Overview,
  FinanceTab.Forecast,
  FinanceTab.ProfitAndLoss,
  FinanceTab.Contracts,
  FinanceTab.EbitAnalysis,
];

const FinanceContent = () => {
  const { t } = useTranslation('common');
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as FinanceTab) || FinanceTab.Overview;

  const handleTabChange = useCallback((_e: React.SyntheticEvent, value: FinanceTab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', value);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  return (
    <PageArea>
      <PageTitle subTitle={t('financePerformanceSubtitle', 'Financial forecast, P&L, and contract/company performance in one place')}>
        {t('financePerformanceTitle', 'Finance Performance')}
      </PageTitle>

      <FinanceFilterBar />

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 4, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label={t('financeTabOverview', 'Overview')}
          value={FinanceTab.Overview} />
        <Tab label={t('financeTabForecast', 'Forecast')}
          value={FinanceTab.Forecast} />
        <Tab label={t('financeTabProfitAndLoss', 'Profit & Loss')}
          value={FinanceTab.ProfitAndLoss} />
        <Tab label={t('financeTabContracts', 'Contracts')}
          value={FinanceTab.Contracts} />
        <Tab label={t('financeTabEbitAnalysis', 'EBIT Analysis')}
          value={FinanceTab.EbitAnalysis} />
      </Tabs>

      {!TAB_ORDER.includes(activeTab) && <OverviewTab />}
      {activeTab === FinanceTab.Overview && <OverviewTab />}
      {activeTab === FinanceTab.Forecast && <ForecastTab />}
      {activeTab === FinanceTab.ProfitAndLoss && <ProfitAndLossTab />}
      {activeTab === FinanceTab.Contracts && <ContractsTab />}
      {activeTab === FinanceTab.EbitAnalysis && <EbitAnalysisTab />}
    </PageArea>
  );
};

const FinancePerformance = () => (
  <FinanceFiltersProvider>
    <FinanceContent />
  </FinanceFiltersProvider>
);

export default FinancePerformance;
